import { Accordion } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { DefaultButton, DownloadButton } from "@/components/ui/button";
import { getBadgeVariant } from "@/utils/helper/getBadgeVariant";
import { useOPMFDTs } from "@/api/opm-fdt";
import { OPMFATData, useInfiniteOPMFATs } from "@/api/opm-fat";
import {
  GenerateABDFATData,
  useGenerateABDFATsCount,
  useInfiniteGenerateABDFATs,
} from "@/api/generate-abd-fat";
import {
  useGenerateABDFDTs,
  useGenerateABDFDTsCount,
} from "@/api/generate-abd-fdt";
import { DataInfo, DataInfoWithBadge } from "@/components/ui/data-info";
import {
  useDownloadOPMByRFS,
  useDownloadOPMZipByRFS,
  useRFS,
  useRFSStatus,
} from "@/api/rfs";
import { useSignalOPM } from "@/api/master-param";
import { H2 } from "@/components/ui/typography";
import { FDTAccordionItems } from "./FDTAccordionItems";
import { FATAccordionItems } from "./FATAccordionItems";
import { canDownload } from "./utils/canDownload";
import { useEffect, useMemo, useState } from "react";
import { useOpmXlsxDownload } from "./hooks/useOpmXlsxDownload";
import OPMCheckingProfile from "./OPMCheckingProfile";
import OPMStatusValidation from "./OPMStatusValidation";
import { ValidationProvider } from "./providers/ValidationProvider";
import OPMEvidence from "./OPMEvidence";
import { useGenerateABDPoles } from "@/api/generate-abd-pole";
import PoleAccordionItems from "./PoleAccordionItems";
import { usePoles } from "@/api/opm-pole";
import { AllDataSources } from "./utils/dataMappers";
import { useOpmEvidences } from "@/api/opm-evidence";

export function OPMCreateForm({ id }: { id: string }) {
  const [page, setPage] = useState(0);
  const [, setKey] = useState(0);
  const [abdFatDataRef, setAbdFatDataRef] = useState<GenerateABDFATData[]>([]);
  const [opmFatDataRef, setOpmFatDataRef] = useState<OPMFATData[]>([]);

  const { data: rfs } = useRFS({ id });
  const { data: rfsStatus } = useRFSStatus({ id });
  const { data: opmFdts, refetch: refetchOpmFdts } = useOPMFDTs({ rfsId: id });
  const {
    data: opmFats,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteOPMFATs({
    opmFdtRfsID: id,
  });
  const { data: opmPoles } = usePoles({ rfsId: id });
  const { data: evidences } = useOpmEvidences({ rfsId: id });

  const { data: abdFdtCount } = useGenerateABDFDTsCount({ rfsId: id });
  const { data: abdFatCount } = useGenerateABDFATsCount({ rfsId: id });
  const { data: abdFdts } = useGenerateABDFDTs({ rfsId: id });
  const { data: abdFats, isPending } = useInfiniteGenerateABDFATs({
    rfsId: id,
    page: String(page),
  });
  const { data: abdPoles } = useGenerateABDPoles({ rfsId: id });

  const { data: signalOPMMinimum } = useSignalOPM({
    paramType: "SIGNAL_OPM_MINIMUM",
  });
  const { data: signalOPMMaximum } = useSignalOPM({
    paramType: "SIGNAL_OPM_MAXIMUM",
  });

  const { mutate: downloadOPM, isPending: isDownloadingOPM } =
    useDownloadOPMByRFS();
  const { mutate: downloadOPMZip, isPending: isDownloadingOPMZip } =
    useDownloadOPMZipByRFS();
  const {
    status: opmXlsxStatus,
    isDownloadingOPMXlsx,
    handleDownloadOPMXlsx,
  } = useOpmXlsxDownload({ id, clusterId: rfs?.clusterId });

  const [expanded, setExpanded] = useState<string[]>([]);
  const totalCollapsible =
    (abdFdts?.length ?? 0) +
    (abdFatDataRef?.length ?? 0) +
    (abdPoles?.length ?? 0);

  const isDownloadable = canDownload(
    opmFdts,
    opmFatDataRef,
    abdFdts,
    abdFatCount,
  );

  const checkingProfileId =
    abdFdts?.[0]?.rfs?.trackerdataApd?.checkingProfile?.id;
  const dataSources: AllDataSources = useMemo(() => {
    return {
      abdFdts,
      opmFdts,
      opmFats: opmFatDataRef,
      abdFatCount,
      opmEvidences: evidences,
      opmPoles,
      abdPoleCount: abdPoles?.length,
    };
  }, [
    abdFdts,
    opmFdts,
    opmFatDataRef,
    abdFatCount,
    evidences,
    opmPoles,
    abdPoles,
  ]);

  useEffect(() => {
    if (abdFats?.content) {
      setAbdFatDataRef((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newContent = abdFats.content.filter(
          (item) => !existingIds.has(item.id),
        );
        return [...prev, ...newContent];
      });
    }
    if (opmFats?.pages) {
      const allContent = opmFats.pages.flatMap((page) => page.content);
      setOpmFatDataRef(allContent);
    }
  }, [abdFats, opmFats]);

  useEffect(() => {
    if (!hasNextPage) return;

    fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  function handleDownloadOPM() {
    downloadOPM({ id, name: `${rfs?.projectName}.zip` });
  }

  function handleDownloadOPMZip() {
    downloadOPMZip({ id, name: `ZIP-${rfs?.clusterId}.zip` });
  }

  function handleExpandAll() {
    setExpanded(
      (abdFdts?.map((abdFdt) => abdFdt.id) ?? []).concat(
        abdFatDataRef?.map((abdFat) => abdFat.id) ?? [],
        abdPoles?.map((abdPole) => abdPole.id) ?? [],
      ),
    );
  }

  function handleCollapseAll() {
    setExpanded([]);
  }

  function handleToggleAccordion(values: string[]) {
    setExpanded(values);
  }

  return (
    <ValidationProvider
      checkingProfileId={checkingProfileId}
      dataSources={dataSources}
    >
      <div className="space-y-4">
        <H2>OPM - {rfs?.clusterId}</H2>
        <div className="grid grid-cols-3">
          <div className="grid grid-rows-3 gap-8">
            <DataInfo label="Cluster ID" value={rfs?.clusterId} />
            <DataInfo label="Project Name" value={rfs?.projectName} />
            <div className="flex gap-8">
              <DataInfoWithBadge
                label="Document Status"
                value={rfsStatus?.opmStatus ?? rfs?.opmStatus ?? "Unvalidated"}
              />
              <OPMStatusValidation />
            </div>
          </div>
          <div className="grid grid-rows-3 gap-8">
            <DataInfo label="Total FDT" value={abdFdtCount} />
            <DataInfo label="Total FAT" value={abdFatCount} />
          </div>
          <div className="ml-auto grid grid-rows-3 justify-items-end gap-8">
            <DownloadButton
              disabled={!isDownloadable || isDownloadingOPMZip}
              onClick={handleDownloadOPMZip}
              loading={isDownloadingOPMZip}
            >
              Download OPM (zip)
            </DownloadButton>
            <DownloadButton
              disabled={!isDownloadable || isDownloadingOPM}
              onClick={handleDownloadOPM}
              loading={isDownloadingOPM}
            >
              Download OPM All Photos
            </DownloadButton>

            <div className="flex items-center justify-end gap-2">
              <Badge variant={getBadgeVariant(opmXlsxStatus)}>
                {opmXlsxStatus}
              </Badge>
              <DownloadButton
                disabled={!isDownloadable || isDownloadingOPMXlsx}
                onClick={handleDownloadOPMXlsx}
                loading={isDownloadingOPMXlsx}
              >
                Download OPM (xlsx)
              </DownloadButton>
            </div>
          </div>
        </div>
        <DataInfo label="Terms">
          <ol className="list-inside list-decimal dark:text-white">
            <li>{`The signal on Output FAT should be in the range ${signalOPMMinimum?.content[0].value} to ${signalOPMMaximum?.content[0].value}`}</li>
            <li>Use a dot (.) as the decimal separator in the FAT Output</li>
            <li>Images must contain metadata</li>
            <li>The image must be clear</li>
            <li>FDT/FAT Photo must contain the displayed FDT/FAT ID.</li>
          </ol>
        </DataInfo>

        <OPMCheckingProfile />

        <div className="flex justify-end">
          {totalCollapsible === expanded.length ? (
            <DefaultButton
              mood="axiata-blue-outline"
              onClick={handleCollapseAll}
            >
              Collapse All
            </DefaultButton>
          ) : (
            <DefaultButton mood="axiata-blue-outline" onClick={handleExpandAll}>
              Expand All
            </DefaultButton>
          )}
        </div>
        <Accordion
          type="multiple"
          className="w-full"
          value={expanded}
          onValueChange={handleToggleAccordion}
        >
          <FDTAccordionItems
            abdFdts={abdFdts}
            opmFdts={opmFdts}
            id={id}
            refetchOpmFdts={refetchOpmFdts}
          />
          <FATAccordionItems
            abdFats={abdFatDataRef}
            opmFats={opmFatDataRef}
            opmFdts={opmFdts}
            id={id}
            setKey={setKey}
          />
          <PoleAccordionItems abdPoles={abdPoles} opmPoles={opmPoles} id={id} />
        </Accordion>
        {page + 1 !== abdFats?.totalPages && (
          <DefaultButton
            mood="axiata-blue-outline"
            className="mx-auto"
            loading={isPending}
            onClick={() => {
              setPage((prev) => prev + 1);
              fetchNextPage();
            }}
          >
            {isPending ? "Loading..." : "Load More"}
          </DefaultButton>
        )}

        <OPMEvidence />
      </div>
    </ValidationProvider>
  );
}
